/* Essenza 1893 — script del tema (vanilla JS, nessuna dipendenza) */
(() => {
  'use strict';

  const theme = window.theme || {};
  const qs = (sel, el = document) => el.querySelector(sel);
  const qsa = (sel, el = document) => Array.from(el.querySelectorAll(sel));
  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents) => {
    const format = theme.moneyFormat || '€{{amount_with_comma_separator}}';
    const value = (Number(cents) || 0) / 100;
    const withDelimiters = (number, decimals, thousands, decimal) => {
      const fixed = number.toFixed(decimals).split('.');
      fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed.join(decimals ? decimal : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount': return withDelimiters(value, 2, ',', '.');
        case 'amount_no_decimals': return withDelimiters(value, 0, ',', '.');
        case 'amount_with_comma_separator': return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return withDelimiters(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return withDelimiters(value, 2, "'", '.');
        default: return withDelimiters(value, 2, '.', ',');
      }
    });
  };

  const toast = (message) => {
    const el = qs('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.hidden = true; }, 3200);
  };

  const fetchSections = async (url, sectionIds) => {
    const sep = url.includes('?') ? '&' : '?';
    const res = await fetch(`${url}${sep}sections=${sectionIds.join(',')}`);
    return res.json();
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------------------------------------------------------------------
     Focus trap e blocco scroll per drawer e modali
     --------------------------------------------------------------------- */
  const focusable = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const trapFocus = (container) => {
    const handler = (e) => {
      if (e.key !== 'Tab') return;
      const items = qsa(focusable, container).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  let openLayers = 0;
  const lockScroll = () => { openLayers += 1; document.body.classList.add('has-drawer'); };
  const unlockScroll = () => { openLayers = Math.max(0, openLayers - 1); if (!openLayers) document.body.classList.remove('has-drawer'); };

  /* ---------------------------------------------------------------------
     Drawer generico (menu mobile, filtri)
     --------------------------------------------------------------------- */
  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      this.panel = qs('.drawer__panel', this);
      this.addEventListener('click', (e) => { if (e.target.closest('[data-drawer-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      document.addEventListener('click', (e) => {
        const opener = e.target.closest(`[data-drawer-open="${this.id}"]`);
        if (opener) { e.preventDefault(); this.open(opener); }
      });
    }
    open(opener) {
      if (this.classList.contains('is-open')) return;
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'true');
      lockScroll();
      this.releaseTrap = trapFocus(this);
      requestAnimationFrame(() => (this.panel || this).focus({ preventScroll: true }));
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.opener && this.opener.setAttribute) this.opener.setAttribute('aria-expanded', 'false');
      unlockScroll();
      if (this.releaseTrap) this.releaseTrap();
      if (this.opener && this.opener.focus) this.opener.focus({ preventScroll: true });
    }
  }
  customElements.define('drawer-panel', DrawerPanel);

  /* ---------------------------------------------------------------------
     Header: fisso, nascosto scorrendo, trasparente sulla hero, mega menu
     --------------------------------------------------------------------- */
  class HeaderWrapper extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.section-header') || this;
      this.mode = this.dataset.sticky;
      this.lastY = window.scrollY;
      if (this.mode === 'none') this.section.classList.add('is-static');
      this.setHeight();
      window.addEventListener('resize', () => this.setHeight(), { passive: true });
      window.addEventListener('scroll', () => this.onScroll(), { passive: true });
      this.onScroll();
      this.initMega();
    }
    setHeight() {
      const h = this.offsetHeight;
      document.documentElement.style.setProperty('--header-h', `${h}px`);
      document.documentElement.style.setProperty('--header-height', `${h}px`);
    }
    onScroll() {
      const y = window.scrollY;
      const scrolled = y > 10;
      this.classList.toggle('is-scrolled', scrolled);
      if (this.mode === 'scroll-up') {
        const hide = y > this.lastY && y > this.offsetHeight * 2 && !this.classList.contains('menu-open');
        this.section.classList.toggle('is-hidden', hide);
        document.documentElement.style.setProperty('--sticky-offset', hide ? '0px' : `${this.offsetHeight}px`);
      } else if (this.mode === 'always') {
        document.documentElement.style.setProperty('--sticky-offset', `${this.offsetHeight}px`);
      }
      this.lastY = y;
    }
    initMega() {
      const items = qsa('[data-mega]', this);
      const closeAll = () => items.forEach((i) => this.closeItem(i));
      items.forEach((item) => {
        const trigger = qs('.nav__link', item);
        let timer;
        let openedByHover = false;
        const open = (byHover) => {
          clearTimeout(timer);
          items.forEach((other) => other !== item && this.closeItem(other));
          if (!item.classList.contains('is-open')) openedByHover = byHover;
          item.classList.add('is-open');
          trigger.setAttribute('aria-expanded', 'true');
          this.classList.add('menu-open');
          this.megaScrollY = window.scrollY;
        };
        // Col mouse il pannello si apre passandoci sopra e si chiude uscendo; al tocco si apre e chiude toccando la voce.
        item.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') open(true); });
        item.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'mouse') timer = setTimeout(() => this.closeItem(item), 180);
        });
        trigger.addEventListener('click', (e) => {
          const isOpen = item.classList.contains('is-open');
          if (isOpen && openedByHover) return; // aperto col mouse: il clic porta alla pagina della voce
          e.preventDefault();
          if (isOpen) this.closeItem(item);
          else open(false);
        });
        qsa('[data-mega-close]', item).forEach((btn) => btn.addEventListener('click', () => {
          this.closeItem(item);
          trigger.focus({ preventScroll: true });
        }));
        item.addEventListener('focusout', (e) => { if (e.relatedTarget && !item.contains(e.relatedTarget)) this.closeItem(item); });
        item.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.closeItem(item); trigger.focus(); } });
      });
      const overlay = qs('.mega-overlay', this);
      if (overlay) overlay.addEventListener('click', closeAll);
      document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.classList.contains('menu-open')) closeAll(); });
      window.addEventListener('scroll', () => {
        if (this.classList.contains('menu-open') && Math.abs(window.scrollY - (this.megaScrollY || 0)) > 80) closeAll();
      }, { passive: true });
    }
    closeItem(item) {
      item.classList.remove('is-open');
      const trigger = qs('.nav__link', item);
      if (trigger) trigger.setAttribute('aria-expanded', 'false');
      if (!qs('[data-mega].is-open', this)) this.classList.remove('menu-open');
    }
  }
  customElements.define('header-wrapper', HeaderWrapper);

  /* Barra annunci a rotazione */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.announcement__item', this);
      if (this.items.length < 2) return;
      this.index = 0;
      const interval = (parseInt(this.dataset.interval, 10) || 5) * 1000;
      this.timer = setInterval(() => this.next(), interval);
      this.addEventListener('mouseenter', () => clearInterval(this.timer));
      this.addEventListener('mouseleave', () => { this.timer = setInterval(() => this.next(), interval); });
    }
    next() {
      const current = this.items[this.index];
      this.index = (this.index + 1) % this.items.length;
      const next = this.items[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      setTimeout(() => current.classList.remove('is-leaving'), 900);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------------------------------------------------------------------
     Ricerca con risultati istantanei
     --------------------------------------------------------------------- */
  class SearchModal extends HTMLElement {
    connectedCallback() {
      this.input = qs('input[type="search"]', this);
      this.results = qs('[data-predictive-results]', this);
      document.addEventListener('click', (e) => {
        if (e.target.closest('[data-search-open]')) { e.preventDefault(); this.open(); }
      });
      this.addEventListener('click', (e) => { if (e.target.closest('[data-search-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      if (this.input && this.input.hasAttribute('data-predictive-input')) {
        let t;
        this.input.addEventListener('input', () => {
          clearTimeout(t);
          t = setTimeout(() => this.search(this.input.value.trim()), 220);
        });
      }
    }
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      this.release = trapFocus(this);
      setTimeout(() => this.input && this.input.focus(), 80);
    }
    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.release) this.release();
    }
    async search(term) {
      if (!this.results) return;
      if (term.length < 2) { this.results.innerHTML = ''; return; }
      this.abort && this.abort.abort();
      this.abort = new AbortController();
      try {
        const url = `${theme.routes.predictiveSearch}?q=${encodeURIComponent(term)}&section_id=predictive-search&resources[type]=product,collection,page,article&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { signal: this.abort.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = parseHTML(html);
        const content = qs('[data-predictive-content]', doc);
        this.results.innerHTML = content ? content.innerHTML : '';
      } catch (err) {
        if (err.name !== 'AbortError') this.results.innerHTML = '';
      }
    }
  }
  customElements.define('search-modal', SearchModal);

  /* ---------------------------------------------------------------------
     Carrello: pannello laterale e pagina, aggiornati con Section Rendering
     --------------------------------------------------------------------- */
  const Cart = {
    sectionIds() {
      const ids = [];
      if (qs('cart-drawer')) ids.push('cart-drawer');
      const page = qs('[data-cart-page]');
      if (page) ids.push(page.dataset.sectionId);
      return ids;
    },
    async add(formData) {
      const ids = this.sectionIds();
      if (ids.length) formData.append('sections', ids.join(','));
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      this.render(data.sections);
      return data;
    },
    async change(line, quantity) {
      const ids = this.sectionIds();
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: ids, sections_url: window.location.pathname })
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || theme.strings.error);
      this.render(data.sections);
      this.updateCount(data.item_count);
      return data;
    },
    async note(value) {
      await fetch(`${theme.routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value })
      });
    },
    render(sections) {
      if (!sections) return;
      const drawer = qs('cart-drawer');
      if (drawer && sections['cart-drawer']) drawer.renderFrom(sections['cart-drawer']);
      const page = qs('[data-cart-page]');
      if (page && sections[page.dataset.sectionId]) {
        const doc = parseHTML(sections[page.dataset.sectionId]);
        const fresh = qs('[data-cart-page]', doc);
        if (fresh) page.innerHTML = fresh.innerHTML;
        const count = fresh ? fresh.dataset.cartCountValue : null;
        if (count !== null && count !== undefined) this.updateCount(count);
      }
    },
    updateCount(count) {
      qsa('[data-cart-count]').forEach((el) => {
        el.dataset.count = count;
        el.textContent = Number(count) > 0 ? count : '';
      });
    }
  };
  window.EssenzaCart = Cart;

  class CartDrawer extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-cart-open]');
        if (opener) { e.preventDefault(); this.open(opener); }
      });
      this.bindLines();
    }
    renderFrom(html) {
      const doc = parseHTML(html);
      const fresh = qs('cart-drawer', doc);
      if (!fresh) return;
      const scroll = qs('[data-cart-body]', this)?.scrollTop || 0;
      this.panel.innerHTML = qs('.drawer__panel', fresh).innerHTML;
      const body = qs('[data-cart-body]', this);
      if (body) body.scrollTop = scroll;
      Cart.updateCount(fresh.dataset.cartCountValue);
    }
    bindLines() {
      bindCartLines(this);
    }
  }
  customElements.define('cart-drawer', CartDrawer);

  function bindCartLines(root) {
    let noteTimer;
    root.addEventListener('change', (e) => {
      const input = e.target.closest('[data-line-input]');
      if (input) updateLine(input.closest('.cart-item'), parseInt(input.dataset.lineInput, 10), parseInt(input.value, 10) || 0);
      const note = e.target.closest('[data-cart-note]');
      if (note) Cart.note(note.value);
    });
    root.addEventListener('input', (e) => {
      const note = e.target.closest('[data-cart-note]');
      if (!note) return;
      clearTimeout(noteTimer);
      noteTimer = setTimeout(() => Cart.note(note.value), 600);
    });
    root.addEventListener('click', (e) => {
      const remove = e.target.closest('[data-line-remove]');
      if (remove) { e.preventDefault(); updateLine(remove.closest('.cart-item'), parseInt(remove.dataset.lineRemove, 10), 0); }
    });
  }

  async function updateLine(itemEl, line, quantity) {
    if (itemEl) itemEl.classList.add('is-updating');
    try {
      await Cart.change(line, quantity);
    } catch (err) {
      toast(err.message || theme.strings.error);
      if (itemEl) itemEl.classList.remove('is-updating');
    }
  }

  const cartPage = qs('[data-cart-page]');
  if (cartPage) bindCartLines(cartPage.parentElement || cartPage);

  /* Quantità +/- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = qs('input', this);
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('button');
        if (!btn || !this.input) return;
        e.preventDefault();
        const step = parseInt(this.input.step, 10) || 1;
        const min = parseInt(this.input.min, 10) || 0;
        const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
        const value = parseInt(this.input.value, 10) || 0;
        const next = btn.name === 'plus' ? Math.min(max, value + step) : Math.max(min, value - step);
        if (next === value) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* Aggiunta al carrello (pagina prodotto, card, upsell) */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      if (theme.cartType === 'page' && !qs('cart-drawer')) return;
      e.preventDefault();
      const submitter = e.submitter || qs('[type="submit"]', this.form);
      if (submitter && (submitter.disabled || submitter.getAttribute('aria-disabled') === 'true')) return;
      const formData = submitter ? new FormData(this.form, submitter) : new FormData(this.form);
      const errorBox = qs('[data-form-error]', this.closest('section') || document);
      if (errorBox) errorBox.hidden = true;
      if (submitter) submitter.classList.add('is-loading');
      try {
        await Cart.add(formData);
        const drawer = qs('cart-drawer');
        if (drawer) drawer.open(submitter);
        else window.location.href = theme.routes.cart;
      } catch (err) {
        if (errorBox) { errorBox.textContent = err.message; errorBox.hidden = false; }
        else toast(err.message);
      } finally {
        if (submitter) submitter.classList.remove('is-loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------------------------------------------------------------------
     Pagina prodotto: varianti, galleria, lightbox, barra fissa
     --------------------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = qs('script[type="application/json"]', this);
      if (!json) return;
      this.variants = JSON.parse(json.textContent);
      this.section = this.closest('[data-product-section]');
      this.addEventListener('change', () => this.onChange());
      this.markAvailability();
    }
    selectedOptions() {
      return qsa('fieldset', this).map((fs) => {
        const checked = qs('input:checked', fs);
        return checked ? checked.value : null;
      });
    }
    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      this.markAvailability();
      qsa('fieldset', this).forEach((fs) => {
        const label = qs('[data-selected-value]', fs);
        const checked = qs('input:checked', fs);
        if (label) label.textContent = checked ? (checked.dataset.label || checked.value) : '';
      });
      if (!variant) { this.setButton(false, theme.strings.unavailable); return; }
      this.variant = variant;
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
      qsa('input[name="id"]', this.section).forEach((input) => { input.value = variant.id; input.dispatchEvent(new Event('change', { bubbles: true })); });
      this.updatePrice(variant);
      this.setButton(variant.available, variant.available ? theme.strings.addToCart : theme.strings.soldOut);
      this.updateInventory(variant);
      if (variant.featured_media) {
        const gallery = qs('product-gallery', this.section);
        if (gallery) gallery.goToMedia(variant.featured_media.id);
      }
      this.section.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
    }
    markAvailability() {
      const fieldsets = qsa('fieldset', this);
      const selected = this.selectedOptions();
      fieldsets.forEach((fs, index) => {
        qsa('input', fs).forEach((input) => {
          const candidate = selected.slice();
          candidate[index] = input.value;
          const match = this.variants.find((v) => v.options.every((o, i) => o === candidate[i]));
          input.classList.toggle('is-unavailable', !match || !match.available);
        });
      });
    }
    updatePrice(variant) {
      qsa('[data-product-price]', this.section).forEach((box) => {
        const sale = variant.compare_at_price && variant.compare_at_price > variant.price;
        box.innerHTML = `<div class="price${sale ? ' price--sale' : ''}"><span class="price__current">${formatMoney(variant.price)}</span>${sale ? `<s class="price__compare">${formatMoney(variant.compare_at_price)}</s>` : ''}</div>`;
      });
    }
    setButton(enabled, text) {
      qsa('[data-add-button]', this.section).forEach((btn) => {
        btn.disabled = !enabled;
        const label = qs('[data-add-label]', btn) || btn;
        label.textContent = text;
      });
    }
    updateInventory(variant) {
      const note = qs('[data-inventory-note]', this.section);
      if (!note) return;
      const data = (this.inventory = this.inventory || JSON.parse(qs('script[data-inventory]', this.section)?.textContent || '{}'));
      const info = data[variant.id];
      if (!info) { note.hidden = true; return; }
      note.hidden = false;
      note.className = `inventory-note inventory-note--${info.state}`;
      note.textContent = info.text;
    }
  }
  customElements.define('variant-picker', VariantPicker);

  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = qs('.gallery__track', this);
      this.slides = qsa('.gallery__slide', this);
      this.thumbs = qsa('.gallery__thumb', this);
      this.dots = qsa('.gallery__dots button', this);
      this.counter = qs('[data-gallery-current]', this);
      if (!this.track) return;
      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.go(i)));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
      qs('[data-gallery-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      qs('[data-gallery-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      this.index = 0;
      let raf;
      this.track.addEventListener('scroll', () => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          if (this.track.scrollWidth <= this.track.clientWidth + 2) return;
          const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
          this.setActive(i);
        });
      }, { passive: true });
      this.slides.forEach((slide, i) => {
        const btn = qs('[data-zoom]', slide);
        if (btn) btn.addEventListener('click', () => this.openLightbox(i));
      });
    }
    go(i) {
      const n = this.slides.length;
      const index = (i + n) % n;
      const horizontal = this.track.scrollWidth > this.track.clientWidth + 2;
      if (horizontal) this.track.scrollTo({ left: index * this.track.clientWidth, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      else this.slides[index].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      this.setActive(index);
    }
    goToMedia(mediaId) {
      const i = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (i > -1) this.go(i);
    }
    setActive(i) {
      this.index = i;
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', j === i ? 'true' : 'false'));
      this.dots.forEach((d, j) => d.setAttribute('aria-current', j === i ? 'true' : 'false'));
      if (this.counter) this.counter.textContent = String(i + 1);
      const thumb = this.thumbs[i];
      if (thumb && thumb.parentElement) {
        const list = thumb.closest('.gallery__thumbs');
        if (list && list.scrollWidth > list.clientWidth) thumb.scrollIntoView({ block: 'nearest', inline: 'center' });
      }
      qsa('video', this).forEach((v, j) => { if (!v.closest('.gallery__slide')?.isSameNode(this.slides[i])) v.pause(); });
    }
    openLightbox(i) {
      const box = qs('.lightbox', this);
      if (!box) return;
      box.classList.add('is-open');
      lockScroll();
      const track = qs('.lightbox__track', box);
      qsa('img[data-src]', box).forEach((img) => { img.src = img.dataset.src; img.removeAttribute('data-src'); });
      requestAnimationFrame(() => { track.scrollLeft = i * track.clientWidth; qs('.lightbox__close', box).focus(); });
      const close = () => { box.classList.remove('is-open'); unlockScroll(); document.removeEventListener('keydown', onKey); };
      const onKey = (e) => { if (e.key === 'Escape') close(); };
      document.addEventListener('keydown', onKey);
      qs('.lightbox__close', box).onclick = close;
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Barra di acquisto fissa quando il pulsante principale esce dallo schermo */
  class StickyBuy extends HTMLElement {
    connectedCallback() {
      const section = this.closest('[data-product-section]');
      const target = section && qs('[data-main-buy]', section);
      if (!target || !('IntersectionObserver' in window)) return;
      const footer = qs('.section-footer');
      let pastButton = false;
      let footerVisible = false;
      const update = () => this.classList.toggle('is-visible', pastButton && !footerVisible);
      new IntersectionObserver(([entry]) => {
        pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        update();
      }).observe(target);
      if (footer) new IntersectionObserver(([entry]) => { footerVisible = entry.isIntersecting; update(); }).observe(footer);
      qs('[data-sticky-buy]', this)?.addEventListener('click', (e) => {
        const btn = qs('[data-main-buy] [data-add-button]', section);
        if (btn && !btn.disabled) { e.preventDefault(); btn.click(); }
      });
      section.addEventListener('variant:change', (e) => {
        const v = e.detail.variant;
        const price = qs('[data-sticky-price]', this);
        if (price) price.textContent = formatMoney(v.price);
        const btn = qs('[data-sticky-buy]', this);
        if (btn) { btn.disabled = !v.available; btn.textContent = v.available ? theme.strings.addToCart : theme.strings.soldOut; }
      });
    }
  }
  customElements.define('sticky-buy', StickyBuy);

  /* Prodotti consigliati e abbinati (API raccomandazioni) */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const load = async () => {
        try {
          const res = await fetch(this.dataset.url);
          const html = await res.text();
          const doc = parseHTML(html);
          const fresh = qs(`product-recommendations[data-section-id="${this.dataset.sectionId}"]`, doc) || qs('product-recommendations', doc);
          if (fresh && fresh.innerHTML.trim().length && qs('[data-product-card], .upsell-item', fresh)) {
            this.innerHTML = fresh.innerHTML;
            this.hidden = false;
            initReveal(this);
          } else {
            this.dismiss();
          }
        } catch (e) { this.dismiss(); }
      };
      if (!('IntersectionObserver' in window)) { load(); return; }
      // L'elemento è nascosto finché non arriva il contenuto: si osserva il contenitore visibile
      const target = this.closest('.shopify-section') || this.parentElement || this;
      new IntersectionObserver((entries, obs) => {
        if (entries[0].isIntersecting) { obs.disconnect(); load(); }
      }, { rootMargin: '0px 0px 600px 0px' }).observe(target);
    }
  }
  ProductRecommendations.prototype.dismiss = function () {
    const section = this.hasAttribute('data-remove-section') ? this.closest('.shopify-section') : null;
    if (section && !window.Shopify?.designMode) section.remove();
    else this.remove();
  };
  customElements.define('product-recommendations', ProductRecommendations);

  /* Introduzione prodotto: mostra le prime righe con "Leggi di più" */
  class ReadMore extends HTMLElement {
    connectedCallback() {
      this.body = qs('[data-read-more-body]', this);
      this.toggle = qs('[data-read-more-toggle]', this);
      if (!this.body || !this.toggle) return;
      this.classList.add('is-clamped');
      requestAnimationFrame(() => {
        if (this.body.scrollHeight - this.body.clientHeight > 24) {
          this.toggle.hidden = false;
        } else {
          this.classList.remove('is-clamped');
        }
      });
      this.toggle.addEventListener('click', () => {
        const open = this.classList.toggle('is-clamped') === false;
        qs('[data-more]', this.toggle).hidden = open;
        qs('[data-less]', this.toggle).hidden = !open;
        this.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      });
    }
  }
  customElements.define('read-more', ReadMore);

  /* ---------------------------------------------------------------------
     Collezione: filtri, ordinamento, carica altri, griglia
     --------------------------------------------------------------------- */
  class CollectionFilters extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.addEventListener('change', (e) => {
        if (e.target.closest('[data-sort]')) { this.submit(e.target.closest('form')); return; }
        const form = e.target.closest('[data-filter-form]');
        if (form && !form.hasAttribute('data-manual')) {
          clearTimeout(this.t);
          this.t = setTimeout(() => this.submit(form), 350);
        }
      });
      this.addEventListener('submit', (e) => {
        const form = e.target.closest('[data-filter-form], [data-sort-form]');
        if (form) { e.preventDefault(); this.submit(form); }
      });
      this.addEventListener('click', (e) => {
        const link = e.target.closest('[data-filter-link]');
        if (link) { e.preventDefault(); this.load(link.href); }
        const grid = e.target.closest('[data-grid]');
        if (grid) this.setGrid(grid.dataset.grid);
        const more = e.target.closest('[data-load-more]');
        if (more) { e.preventDefault(); this.loadMore(more); }
      });
      window.addEventListener('popstate', () => this.load(window.location.href, false));
      try { const g = localStorage.getItem('essenza-grid'); if (g) this.setGrid(g); } catch (e) { /* storage non disponibile */ }
    }
    setGrid(cols) {
      const grid = qs('[data-product-grid]', this);
      if (grid) grid.style.setProperty('--cols-d', cols);
      qsa('[data-grid]', this).forEach((b) => b.setAttribute('aria-pressed', b.dataset.grid === String(cols) ? 'true' : 'false'));
      try { localStorage.setItem('essenza-grid', cols); } catch (e) { /* storage non disponibile */ }
    }
    submit(form) {
      const forms = qsa('[data-filter-form], [data-sort-form]', this);
      const params = new URLSearchParams();
      forms.forEach((f) => new FormData(f).forEach((value, key) => { if (value !== '') params.append(key, value); }));
      this.load(`${window.location.pathname}?${params.toString()}`);
    }
    async load(url, push = true) {
      this.classList.add('is-loading');
      const target = new URL(url, window.location.origin);
      target.searchParams.set('section_id', this.sectionId);
      try {
        const res = await fetch(target.toString());
        const html = await res.text();
        const doc = parseHTML(html);
        const fresh = qs('collection-filters', doc);
        if (fresh) {
          const openGroups = qsa('details[open]', this).map((d) => d.dataset.group);
          const drawerOpen = qs('drawer-panel.is-open', this);
          qsa('[data-swap]', this).forEach((el) => {
            const replacement = qs(`[data-swap="${el.dataset.swap}"]`, fresh);
            if (replacement) el.replaceWith(replacement);
          });
          openGroups.forEach((g) => { const d = qs(`details[data-group="${g}"]`, this); if (d) d.open = true; });
          if (drawerOpen) { /* il drawer resta aperto: i contenuti interni sono stati aggiornati */ }
          initReveal(this);
          try { const g = localStorage.getItem('essenza-grid'); if (g) this.setGrid(g); } catch (e) { /* noop */ }
        }
        if (push) {
          target.searchParams.delete('section_id');
          window.history.pushState({}, '', target.toString());
        }
      } finally {
        this.classList.remove('is-loading');
      }
    }
    async loadMore(button) {
      button.classList.add('is-loading');
      try {
        const target = new URL(button.href, window.location.origin);
        target.searchParams.set('section_id', this.sectionId);
        const res = await fetch(target.toString());
        const doc = parseHTML(await res.text());
        const items = qsa('[data-product-grid] > li', doc);
        const grid = qs('[data-product-grid]', this);
        items.forEach((li) => grid.appendChild(li));
        const fresh = qs('[data-swap="pager"]', doc);
        const current = qs('[data-swap="pager"]', this);
        if (current) { if (fresh) current.replaceWith(fresh); else current.remove(); }
        initReveal(this);
      } finally {
        button.classList.remove('is-loading');
      }
    }
  }
  customElements.define('collection-filters', CollectionFilters);

  /* ---------------------------------------------------------------------
     Sezioni: hero, citazioni, schede, slider, video, popup
     --------------------------------------------------------------------- */
  class HeroSlider extends HTMLElement {
    connectedCallback() {
      this.slides = qsa('.hero__slide', this);
      this.progress = qs('.hero__progress span', this);
      this.label = qs('[data-hero-current]', this);
      this.index = 0;
      if (this.slides.length < 2) return;
      this.delay = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
      qs('[data-hero-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
      qs('[data-hero-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
      this.start();
      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));
    }
    start() {
      if (!this.delay || prefersReducedMotion()) return;
      this.stop();
      this.restartProgress();
      this.timer = setInterval(() => this.go(this.index + 1), this.delay);
    }
    stop() { clearInterval(this.timer); }
    restartProgress() {
      if (!this.progress) return;
      this.progress.classList.remove('is-running');
      void this.progress.offsetWidth;
      this.progress.classList.add('is-running');
    }
    go(i, manual) {
      const n = this.slides.length;
      this.slides[this.index].classList.remove('is-active');
      this.index = (i + n) % n;
      this.slides[this.index].classList.add('is-active');
      if (this.label) this.label.textContent = String(this.index + 1).padStart(2, '0');
      if (manual) this.start(); else this.restartProgress();
    }
  }
  customElements.define('hero-slider', HeroSlider);

  class QuoteSlider extends HTMLElement {
    connectedCallback() {
      this.items = qsa('.quote', this);
      this.dots = qsa('.quotes__nav button', this);
      this.index = 0;
      this.dots.forEach((d, i) => d.addEventListener('click', () => { this.go(i); this.restart(); }));
      this.restart();
    }
    restart() {
      clearInterval(this.timer);
      if (this.items.length > 1 && !prefersReducedMotion()) this.timer = setInterval(() => this.go(this.index + 1), 7000);
    }
    go(i) {
      const n = this.items.length;
      this.items[this.index].classList.remove('is-active');
      this.dots[this.index]?.setAttribute('aria-current', 'false');
      this.index = (i + n) % n;
      this.items[this.index].classList.add('is-active');
      this.dots[this.index]?.setAttribute('aria-current', 'true');
    }
  }
  customElements.define('quote-slider', QuoteSlider);

  class TabsElement extends HTMLElement {
    connectedCallback() {
      this.tabs = qsa('[role="tab"]', this);
      this.panels = qsa('[role="tabpanel"]', this);
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight') { this.select((i + 1) % this.tabs.length); this.tabs[(i + 1) % this.tabs.length].focus(); }
          if (e.key === 'ArrowLeft') { const j = (i - 1 + this.tabs.length) % this.tabs.length; this.select(j); this.tabs[j].focus(); }
        });
      });
      // Apre la scheda indicata nel link e scorre fino ai prodotti: /pages/set-regalo#skincare oppure ?tab=skincare
      // (nome della scheda in italiano o nella lingua del visitatore, handle della collezione o id del blocco).
      // ?tab= arriva dalle voci del menu (snippets/menu-url) e può contenere più nomi separati da virgola.
      this.fromUrl = (useQuery) => {
        const q = useQuery ? new URLSearchParams(window.location.search).get('tab') || '' : '';
        const wants = [...q.split(','), decodeURIComponent(window.location.hash.slice(1))]
          .map((s) => s.toLowerCase().trim()).filter(Boolean).flatMap((s) => [s, s.replace(/-\d+$/, '')]);
        if (!wants.length) return;
        const keys = (t) => [...(t.dataset.anchors || '').toLowerCase().split(/\s+/), t.id.toLowerCase(), (t.getAttribute('aria-controls') || '').toLowerCase()];
        let i = -1;
        for (const w of wants) { i = this.tabs.findIndex((t) => keys(t).includes(w)); if (i >= 0) break; }
        if (i < 0 && !q) return;
        if (i >= 0) this.select(i);
        setTimeout(() => this.scrollToTabs(), 60);
      };
      requestAnimationFrame(() => this.fromUrl(true));
      this.onHash = () => this.fromUrl(false);
      window.addEventListener('hashchange', this.onHash);
    }
    disconnectedCallback() { window.removeEventListener('hashchange', this.onHash); }
    // Porta i nomi delle schede in cima allo schermo, con i prodotti subito sotto
    scrollToTabs() {
      const list = qs('[role="tablist"]', this) || this;
      const header = qs('header-wrapper');
      const mode = header ? header.dataset.sticky : 'none';
      const top = list.getBoundingClientRect().top + window.scrollY;
      // con l'header "scroll-up", scendendo l'header si nasconde: niente spazio da lasciargli
      const covered = mode === 'always' || (mode === 'scroll-up' && top - 16 < window.scrollY);
      const offset = (covered && header ? header.offsetHeight : 0) + 16;
      window.scrollTo({ top: Math.max(0, top - offset), behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }
    select(i) {
      this.tabs.forEach((t, j) => { t.setAttribute('aria-selected', j === i ? 'true' : 'false'); t.tabIndex = j === i ? 0 : -1; });
      this.panels.forEach((p, j) => { p.hidden = j !== i; });
      initReveal(this.panels[i]);
    }
  }
  customElements.define('tabs-element', TabsElement);

  class SliderButtons extends HTMLElement {
    connectedCallback() {
      const section = this.closest('section, .shopify-section') || document;
      this.track = qs('[data-scroller]', section);
      if (!this.track) return;
      this.prev = qs('[data-prev]', this);
      this.next = qs('[data-next]', this);
      const step = () => {
        const item = this.track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || 0) : this.track.clientWidth;
      };
      this.prev?.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
      this.next?.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));
      const update = () => {
        const max = this.track.scrollWidth - this.track.clientWidth - 2;
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
        this.hidden = max <= 0;
      };
      this.track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }
  }
  customElements.define('slider-buttons', SliderButtons);

  class DeferredVideo extends HTMLElement {
    connectedCallback() {
      const btn = qs('[data-play]', this);
      if (!btn) return;
      btn.addEventListener('click', () => {
        const tpl = qs('template', this);
        if (!tpl) return;
        const frame = qs('.video-section__frame', this);
        frame.innerHTML = '';
        frame.appendChild(tpl.content.cloneNode(true));
        const video = qs('video', frame);
        if (video) video.play();
      });
    }
  }
  customElements.define('deferred-video', DeferredVideo);

  class NewsletterPopup extends HTMLElement {
    connectedCallback() {
      this.key = `essenza-popup-${this.dataset.id || 'x'}`;
      const isDesignMode = window.Shopify && window.Shopify.designMode;
      this.addEventListener('click', (e) => { if (e.target.closest('[data-popup-close]')) this.close(); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.close(); });
      const posted = window.location.search.includes('customer_posted=true') && window.location.hash === `#${this.dataset.formId}`;
      if (posted) { this.open(); this.remember(365); return; }
      if (isDesignMode) return;
      let seen = null;
      try { seen = localStorage.getItem(this.key); } catch (e) { /* storage non disponibile */ }
      if (seen && Date.now() < Number(seen)) return;
      const delay = (parseInt(this.dataset.delay, 10) || 8) * 1000;
      setTimeout(() => { if (!document.body.classList.contains('has-drawer')) this.open(); }, delay);
    }
    open() {
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      this.release = trapFocus(this);
      setTimeout(() => qs('input[type="email"], button', this)?.focus({ preventScroll: true }), 120);
    }
    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      if (this.release) this.release();
      this.remember(parseInt(this.dataset.days, 10) || 14);
    }
    remember(days) {
      try { localStorage.setItem(this.key, String(Date.now() + days * 864e5)); } catch (e) { /* storage non disponibile */ }
    }
  }
  customElements.define('newsletter-popup', NewsletterPopup);

  /* Parallasse leggera sui banner */
  const parallaxEls = qsa('[data-parallax]');
  if (parallaxEls.length && !prefersReducedMotion()) {
    const onScroll = () => {
      parallaxEls.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
        el.style.setProperty('--parallax', `${(progress * -6).toFixed(2)}%`);
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* Accordion con apertura morbida */
  qsa('details.accordion').forEach((details) => {
    const summary = qs('summary', details);
    const content = qs('.accordion__content', details);
    if (!summary || !content) return;
    summary.addEventListener('click', (e) => {
      if (prefersReducedMotion()) return;
      e.preventDefault();
      if (details.open) {
        const h = content.offsetHeight;
        content.animate([{ height: `${h}px`, opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 320, easing: 'cubic-bezier(.22,.61,.36,1)' }).onfinish = () => { details.open = false; };
      } else {
        details.open = true;
        const h = content.offsetHeight;
        content.animate([{ height: '0px', opacity: 0 }, { height: `${h}px`, opacity: 1 }], { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    });
  });

  /* ---------------------------------------------------------------------
     Quiz consigli (Concierge): una domanda alla volta, risultato per profilo
     --------------------------------------------------------------------- */
  class QuizFlow extends HTMLElement {
    connectedCallback() {
      this.intro = qs('[data-quiz-intro]', this);
      this.form = qs('[data-quiz-form]', this);
      this.resultsBox = qs('[data-quiz-results]', this);
      if (!this.form) return;
      this.steps = qsa('[data-quiz-step]', this.form);
      this.bar = qs('[data-quiz-bar]', this);
      this.btnNext = qs('[data-quiz-next]', this);
      this.btnBack = qs('[data-quiz-back]', this);
      this.btnSubmit = qs('[data-quiz-submit]', this);
      this.index = 0;
      qs('[data-quiz-start]', this)?.addEventListener('click', () => this.start());
      this.btnNext.addEventListener('click', () => this.go(this.index + 1));
      this.btnBack.addEventListener('click', () => this.go(this.index - 1));
      this.form.addEventListener('change', (e) => {
        if (e.target.type !== 'radio') return;
        this.sync();
        if (this.index < this.steps.length - 1) {
          clearTimeout(this.auto);
          this.auto = setTimeout(() => this.go(this.index + 1), 420);
        }
      });
      this.form.addEventListener('submit', (e) => { e.preventDefault(); this.finish(); });
      this.addEventListener('click', (e) => { if (e.target.closest('[data-quiz-restart]')) this.restart(); });
    }
    start() {
      this.intro.hidden = true;
      this.form.hidden = false;
      this.go(0);
      qs('input', this.steps[0])?.focus({ preventScroll: true });
    }
    go(i) {
      if (i < 0 || i >= this.steps.length) return;
      this.steps.forEach((step, n) => {
        step.hidden = n !== i;
        if (n === i) step.animate([{ opacity: 0, transform: 'translateY(12px)' }, { opacity: 1, transform: 'none' }], { duration: 480, easing: 'cubic-bezier(.16,1,.3,1)' });
      });
      this.index = i;
      this.sync();
    }
    sync() {
      const answered = !!qs('input:checked', this.steps[this.index]);
      const last = this.index === this.steps.length - 1;
      this.btnBack.hidden = this.index === 0;
      this.btnNext.hidden = last;
      this.btnNext.disabled = !answered;
      this.btnSubmit.hidden = !last;
      this.btnSubmit.disabled = !answered;
      const done = qsa('[data-quiz-step]', this.form).filter((s) => qs('input:checked', s)).length;
      if (this.bar) this.bar.style.transform = `scaleX(${done / this.steps.length})`;
    }
    finish() {
      const answers = this.steps.map((s) => qs('input:checked', s)?.value).filter(Boolean);
      const score = {};
      answers.forEach((a) => { score[a] = (score[a] || 0) + 1; });
      const best = Math.max(...Object.values(score));
      const leaders = Object.keys(score).filter((k) => score[k] === best);
      const lastAnswer = answers[answers.length - 1];
      const profile = leaders.includes(lastAnswer) ? lastAnswer : leaders.sort()[0];
      const results = qsa('[data-quiz-result]', this.resultsBox);
      const match = results.find((r) => r.dataset.quizResult === profile) || results[0];
      results.forEach((r) => { r.hidden = r !== match; });
      this.form.hidden = true;
      this.resultsBox.hidden = false;
      if (match) {
        match.animate([{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'none' }], { duration: 640, easing: 'cubic-bezier(.16,1,.3,1)' });
        initReveal(match);
      }
      const top = this.closest('.section') || this;
      top.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    }
    restart() {
      this.form.reset();
      this.resultsBox.hidden = true;
      this.form.hidden = false;
      this.go(0);
    }
  }
  customElements.define('quiz-flow', QuizFlow);

  /* ---------------------------------------------------------------------
     Crea il tuo set regalo: scelta prodotti e aggiunta multipla al carrello
     --------------------------------------------------------------------- */
  class GiftBox extends HTMLElement {
    connectedCallback() {
      this.max = Number(this.dataset.max) || 5;
      this.min = Number(this.dataset.min) || 1;
      this.selected = [];
      this.slots = qsa('[data-gift-slots] > li', this);
      this.submitBtn = qs('[data-gift-submit]', this);
      this.addEventListener('click', (e) => {
        const toggle = e.target.closest('[data-gift-toggle]');
        if (toggle) this.toggle(toggle.closest('[data-gift-pick]'));
        const slot = e.target.closest('[data-gift-remove]');
        if (slot) this.removeAt(Number(slot.dataset.giftRemove));
      });
      this.addEventListener('change', (e) => {
        const select = e.target.closest('select[data-gift-variant]');
        if (!select) return;
        const pick = select.closest('[data-gift-pick]');
        const entry = this.selected.find((s) => s.pick === pick);
        if (entry) { Object.assign(entry, this.read(pick)); this.render(); }
      });
      this.submitBtn?.addEventListener('click', () => this.submit());
      this.render();
    }
    read(pick) {
      const field = qs('[data-gift-variant]', pick);
      const option = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
      return { id: field.value, price: Number(option.dataset.price) || 0, image: option.dataset.image, title: pick.dataset.title };
    }
    toggle(pick) {
      const i = this.selected.findIndex((s) => s.pick === pick);
      if (i > -1) { this.removeAt(i); return; }
      if (this.selected.length >= this.max) {
        this.flash(theme.strings?.giftBoxFull || `Massimo ${this.max} prodotti`);
        return;
      }
      this.selected.push({ pick, ...this.read(pick) });
      this.render();
    }
    removeAt(i) {
      this.selected.splice(i, 1);
      this.render();
    }
    flash(message) {
      const box = qs('[data-gift-error]', this);
      if (!box) return;
      box.textContent = message;
      box.hidden = false;
      clearTimeout(this.flashTimer);
      this.flashTimer = setTimeout(() => { box.hidden = true; }, 3200);
    }
    render() {
      qsa('[data-gift-pick]', this).forEach((pick) => {
        const on = this.selected.some((s) => s.pick === pick);
        pick.classList.toggle('is-selected', on);
        const btn = qs('[data-gift-toggle]', pick);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        qs('[data-label-add]', btn).hidden = on;
        qs('[data-label-remove]', btn).hidden = !on;
      });
      this.slots.forEach((slot, i) => {
        const item = this.selected[i];
        slot.classList.toggle('is-filled', !!item);
        slot.innerHTML = item
          ? `<button type="button" data-gift-remove="${i}" aria-label="${(theme.strings?.remove || 'Rimuovi')} ${item.title}"><img src="${item.image}" alt="" width="80" height="80" loading="lazy"><span class="gift-box__slot-x" aria-hidden="true">×</span></button>`
          : `<span>${i + 1}</span>`;
      });
      const count = this.selected.length;
      const total = this.selected.reduce((sum, s) => sum + s.price, 0);
      qs('[data-gift-count]', this).textContent = count;
      qs('[data-gift-total]', this).textContent = count ? formatMoney(total) : '—';
      const hint = qs('[data-gift-hint]', this);
      if (hint) hint.hidden = count >= this.min;
      if (this.submitBtn) this.submitBtn.disabled = count < this.min;
    }
    async submit() {
      if (this.selected.length < this.min) return;
      const btn = this.submitBtn;
      btn.classList.add('is-loading');
      btn.disabled = true;
      const property = this.dataset.property || 'Set regalo';
      const ref = `#${Date.now().toString(36).slice(-5).toUpperCase()}`;
      const formData = new FormData();
      this.selected.forEach((s, i) => {
        formData.append(`items[${i}][id]`, s.id);
        formData.append(`items[${i}][quantity]`, 1);
        formData.append(`items[${i}][properties][${property}]`, `${ref} · ${i + 1}/${this.selected.length}`);
      });
      try {
        await Cart.add(formData);
        this.selected = [];
        this.render();
        const drawer = qs('cart-drawer');
        if (drawer && theme.cartType === 'drawer') drawer.open(btn);
        else window.location.href = theme.routes.cart;
      } catch (err) {
        this.flash(err.message);
      } finally {
        btn.classList.remove('is-loading');
        this.render();
      }
    }
  }
  customElements.define('gift-box', GiftBox);

  /* ---------------------------------------------------------------------
     Lista dei desideri e visti di recente (salvati nel browser)
     --------------------------------------------------------------------- */
  const store = {
    get(key) { try { return JSON.parse(localStorage.getItem(key)) || []; } catch (e) { return []; } },
    set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage non disponibile */ } }
  };
  const WISH_KEY = 'essenza-wishlist';
  const RECENT_KEY = 'essenza-recent';
  const Wishlist = {
    all() { return store.get(WISH_KEY); },
    has(handle) { return this.all().includes(handle); },
    toggle(handle) {
      const list = this.all();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1); else list.unshift(handle);
      store.set(WISH_KEY, list.slice(0, 60));
      this.sync();
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { handle, active: i === -1 } }));
      return i === -1;
    },
    sync(root = document) {
      const list = this.all();
      qsa('[data-wishlist-toggle]', root).forEach((btn) => {
        const on = list.includes(btn.dataset.handle);
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        const label = on ? btn.dataset.labelRemove : btn.dataset.labelAdd;
        if (label) btn.setAttribute('aria-label', label);
      });
      qsa('[data-wishlist-count]').forEach((el) => { el.textContent = list.length ? list.length : ''; el.dataset.count = list.length; });
    }
  };
  window.EssenzaWishlist = Wishlist;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const active = Wishlist.toggle(btn.dataset.handle);
    if (active) btn.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(1)' }], { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' });
  });
  Wishlist.sync();
  new MutationObserver(() => {
    clearTimeout(Wishlist.t);
    Wishlist.t = setTimeout(() => Wishlist.sync(), 80);
  }).observe(document.body, { childList: true, subtree: true });

  const fetchCard = async (handle) => {
    try {
      const res = await fetch(`${theme.routes.root.replace(/\/$/, '')}/products/${encodeURIComponent(handle)}?section_id=product-card-ajax`);
      if (!res.ok) return null;
      const doc = parseHTML(await res.text());
      return qs('[data-ajax-card]', doc);
    } catch (e) { return null; }
  };

  class WishlistGrid extends HTMLElement {
    connectedCallback() {
      this.list = qs('[data-wishlist-items]', this);
      this.empty = qs('[data-wishlist-empty]', this);
      this.render();
      document.addEventListener('wishlist:change', (e) => {
        if (!e.detail.active) {
          const card = qs(`[data-ajax-card="${CSS.escape(e.detail.handle)}"]`, this.list);
          if (card) card.animate([{ opacity: 1 }, { opacity: 0, transform: 'scale(.96)' }], { duration: 300 }).onfinish = () => { card.remove(); this.toggleEmpty(); };
        }
      });
    }
    toggleEmpty() { this.empty.hidden = qsa('[data-ajax-card]', this.list).length > 0; }
    async render() {
      const handles = Wishlist.all();
      if (!handles.length) { this.toggleEmpty(); return; }
      this.classList.add('is-loading');
      const cards = await Promise.all(handles.map(fetchCard));
      cards.forEach((card, i) => {
        if (card) this.list.appendChild(card);
        else { const list = Wishlist.all().filter((h) => h !== handles[i]); store.set(WISH_KEY, list); }
      });
      this.classList.remove('is-loading');
      Wishlist.sync();
      this.toggleEmpty();
      initReveal(this);
    }
  }
  customElements.define('wishlist-grid', WishlistGrid);

  const recentRoot = qs('[data-recent-handle]');
  if (recentRoot && document.body.dataset.recentlyViewed !== 'false') {
    const handle = recentRoot.dataset.recentHandle;
    const list = store.get(RECENT_KEY).filter((h) => h !== handle);
    list.unshift(handle);
    store.set(RECENT_KEY, list.slice(0, 16));
  }

  class RecentlyViewed extends HTMLElement {
    async connectedCallback() {
      const limit = Number(this.dataset.limit) || 8;
      const handles = store.get(RECENT_KEY).filter((h) => h && h !== this.dataset.exclude).slice(0, limit);
      if (!handles.length) return;
      const cards = (await Promise.all(handles.map(fetchCard))).filter(Boolean);
      if (!cards.length) return;
      const list = qs('[data-recent-items]', this);
      cards.forEach((card) => { card.className = 'scroller__item'; list.appendChild(card); });
      const section = this.closest('[data-recently-viewed-section]');
      if (section) section.hidden = false;
      Wishlist.sync(this);
    }
  }
  customElements.define('recently-viewed', RecentlyViewed);

  /* Comparsa allo scorrimento */
  let revealObserver;
  const revealPending = new Map();
  function initReveal(root = document) {
    if (!document.body.classList.contains('has-reveal')) return;
    const els = qsa('.reveal:not(.is-visible), .reveal-img:not(.is-visible)', root);
    if (!('IntersectionObserver' in window) || prefersReducedMotion()) { els.forEach((el) => el.classList.add('is-visible')); return; }
    revealObserver = revealObserver || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        (revealPending.get(entry.target) || [entry.target]).forEach((el) => el.classList.add('is-visible'));
        revealPending.delete(entry.target);
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px 10% 0px', threshold: 0.01 });
    // Le immagini partono ritagliate (clip-path) e Chrome non le vede come visibili:
    // si osserva il contenitore, che ha sempre la sua area piena.
    els.forEach((el) => {
      const target = el.classList.contains('reveal-img') && el.parentElement ? el.parentElement : el;
      if (!revealPending.has(target)) { revealPending.set(target, []); revealObserver.observe(target); }
      revealPending.get(target).push(el);
    });
  }
  initReveal();

  /* Lingue: i link interni scritti a mano nei testi e nei pulsanti (/pages/...) restano nella lingua scelta */
  function localizeLinks(scope = document) {
    const root = (window.theme && window.theme.routes && window.theme.routes.root) || '/';
    if (root === '/') return;
    const prefix = root.replace(/\/$/, '');
    const skip = /^\/(cdn|checkouts?|apps|services|tools|wpm|web-pixels|recommendations)(\/|$)/;
    qsa('a[href^="/"]', scope).forEach((a) => {
      const href = a.getAttribute('href');
      if (href[1] === '/' || href === prefix || href.startsWith(prefix + '/') || href.startsWith(prefix + '?') || skip.test(href)) return;
      a.setAttribute('href', prefix + href);
    });
  }
  localizeLinks();

  /* Editor del tema: ricarica i comportamenti quando cambia una sezione */
  document.addEventListener('shopify:section:load', (e) => { initReveal(e.target); localizeLinks(e.target); });
  document.addEventListener('shopify:block:select', (e) => {
    const slide = e.target.closest('.hero__slide');
    const slider = e.target.closest('hero-slider');
    if (slide && slider) { slider.stop(); slider.go(qsa('.hero__slide', slider).indexOf(slide)); }
  });
})();
